import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react'
import { Input, Label } from '../../styles/newStyles/Register.styled'
import { Text, useMainContext } from '../../modules/Dashboard'
import VerifyIcon from '../icons/VerifyIcon'

const OTPInput = forwardRef(({ timer, onOtpResend, onOtpChange, singleField = false, inputClassName = '', wrapperClassName = '' }, ref) => {
	const { main: { lang } } = useMainContext()
	const [otp, setOtp] = useState(Array(6).fill(''))
	const [singleInput, setSingleInput] = useState('')
	const inputs = useRef([])

	const toEnglishNumber = (str) => {
		return str.replace(/[۰-۹]/g, (d) => String.fromCharCode(d.charCodeAt(0) - 1728))
	}

	// --- Individual input change ---
	const handleChange = (element, index) => {
		const value = element.value
		if (/^[\d۰-۹]$/.test(value)) {
			const normalizedValue = toEnglishNumber(value)
			const newOtp = [...otp]
			newOtp[index] = normalizedValue
			setOtp(newOtp)

			if (index < 5) inputs.current[index + 1]?.focus()
		} else if (value === '') {
			const newOtp = [...otp]
			newOtp[index] = ''
			setOtp(newOtp)
		}
	}

	const handleKeyDown = (event, index) => {
		if (event.key === 'Backspace' && otp[index] === '') {
			if (index > 0) inputs.current[index - 1]?.focus()
		}
	}

	const handlePaste = (event) => {
		const pasteData = event.clipboardData.getData('text').slice(0, 6)
		if (/^\d+$/.test(pasteData)) {
			const newOtp = pasteData.split('')
			setOtp(newOtp)
			inputs.current[newOtp.length - 1]?.focus()
		}
	}

	// --- Single input field change ---
	const handleSingleInputChange = (e) => {
		const value = toEnglishNumber(e.target.value).slice(0, 6)
		setSingleInput(value)
		setOtp(value.split('')) // Sync with OTP value
	}

	useEffect(() => {
		onOtpChange(otp.join(''))
	}, [otp])

	useImperativeHandle(ref, () => ({
		getOtpValue: () => otp.join(''),
	}))

	useEffect(() => {
		if (!singleField) inputs.current[0]?.focus()
	}, [singleField])

	if (singleField) {
		// Render single input field
		return (
			<div className='flex flex-col gap-1'>
				<Label>
					<VerifyIcon size={16} color='#8481BD' />
					<Text tid={"verification-code"} className='text-inherit' />
				</Label>
				<div className='relative'>
					<Input
						type='text'
						value={singleInput}
						onChange={handleSingleInputChange}
						placeholder={lang === 'fa' ? "کد ۶ رقمی را وارد کنید" : "Enter 6-digit code"}
						maxLength={6}
						className={inputClassName}
						autoFocus
					/>
					<div className='absolute top-1/2 -translate-y-1/2 ltr:right-3 rtl:left-3'>
						{timer > 0 ? (
							<p className='flex items-center text-gray-500 text-sm'>
								<Text tid='new-code' />
								{' : '}
								<span className='text-primaryOrange'>
									{timer}
								</span>
							</p>
						) : (
							<p className='cursor-pointer text-sm' onClick={onOtpResend}>
								<Text tid={'get-code'} className='text-primaryOrange' />
							</p>
						)}
					</div>
				</div>
			</div>
		)
	}

	// Render 6 separate inputs
	return (
		<div className={'flex justify-center gap-2 lg:gap-4 relative' + wrapperClassName} onPaste={handlePaste}>
			<div className='absolute -top-6 rtl:right-3 ltr:left-3'>
				{timer > 0 ? (
					<p className='flex items-center text-gray-500 text-sm'>
						<Text tid='new-code' />
						{' : '}
						<span className='text-primaryOrange'>
							{timer}
						</span>
					</p>
				) : (
					<p className='cursor-pointer text-sm' onClick={onOtpResend}>
						<Text tid={'get-code'} className='text-primaryOrange' />
					</p>
				)}
			</div>
			{otp.map((value, index) => (
				<input
					key={index}
					type='text'
					inputMode="numeric"
					maxLength='1'
					value={value}
					onChange={(e) => handleChange(e.target, index)}
					onKeyDown={(e) => handleKeyDown(e, index)}
					ref={(el) => (inputs.current[index] = el)}
					className={
						'w-10 h-10 lg:w-12 lg:h-12 text-lg text-center rounded-xl border border-borderPrimary bg-gray-200 dark:bg-white/10 outline-none ' +
						inputClassName
					}
				/>
			))}
		</div>
	)
})

export default OTPInput
